/**
 * @file googleAddress.ts
 * @module utils/googleAddress
 * @description Shared Google reverse-geocoding utility for converting latitude/longitude
 * into normalized address metadata used across customer, warehouse, and delivery modules.
 * Provides selected-result parsing, district resolution, and array response formatting.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.1
 * @version 1.2.1
 */

import { APIkey } from './constants';

/**
 * Google address component entry from Geocoding API.
 */
type GoogleAddressComponent = {
  long_name?: string;
  short_name?: string;
  types?: string[];
};

/**
 * Single result item from Google Geocoding API response.
 */
type GoogleGeocodeResult = {
  address_components?: GoogleAddressComponent[];
  formatted_address?: string;
  place_id?: string;
  types?: string[];
  geometry?: {
    location?: {
      lat?: number | string;
      lng?: number | string;
    };
  };
};

/**
 * Top-level Google Geocoding API response payload.
 */
type GoogleGeocodeResponse = {
  status?: string;
  results?: GoogleGeocodeResult[];
};

/**
 * District option record consumed from admin-side district datasets.
 */
type DistrictOption = {
  _id?: string;
  name?: string;
};

/**
 * Normalized address shape extracted from one or more geocode results.
 */
export type ParsedGoogleAddress = {
  wardName: string;
  districtName: string;
  countryName: string;
  localityName: string;
  subLocalityName: string;
  regionName: string;
  routeName: string;
  streetNumber: string;
  postalCode: string;
  /** Admin area level 1 (usually state/region). */
  administrativeAreaLevel1: string;
  /** Admin area level 2 (often district/county depending on country). */
  administrativeAreaLevel2: string;
  /** Admin area level 3 (commonly city/district in some locales). */
  administrativeAreaLevel3: string;
  /** Admin area level 4 (more granular subdivision, sometimes ward-like). */
  administrativeAreaLevel4: string;
  formattedAddress: string;
  placeId: string;
  allFormattedAddresses: string[];
};

/**
 * Full helper response for reverse geocode lookup + parsed data variants.
 */
export type SearchGoogleAddressResult = {
  rawResults: GoogleGeocodeResult[];
  selectedResult: GoogleGeocodeResult | null;
  parsed: ParsedGoogleAddress;
  status: boolean;
  data: GoogleAddressItem[];
  arrayResponse: Array<{
    status: boolean;
    data: Array<{
      raw: {
        ward: string;
        city: string;
        district: string;
        country: string;
        region: string;
        route: string;
        street_number: string;
        postal_code: string;
      };
      full_address: string;
      placeid: string;
    }>;
  }>;
};

/**
 * Address item enriched for UI usage and district-resolution metadata.
 */
export type GoogleAddressItem = {
  status: boolean;
  isSelected: boolean;
  placeId: string;
  fullAddress: string;
  district: string;
  districtName: string;
  wardName: string;
  localityName: string;
  countryName: string;
  lat?: number;
  lng?: number;
  parsed: ParsedGoogleAddress;
  raw: GoogleGeocodeResult;
};

/**
 * Optional configuration for reverse geocode search and district fallback behavior.
 */
export type SearchGoogleAddressOptions = {
  searchText?: string;
  districts?: DistrictOption[];
  defaultDistrictName?: string;
  defaultDistrictId?: string;
};

/**
 * Returns an empty parsed-address object used as a safe default/fallback.
 */
const defaultParsedAddress = (): ParsedGoogleAddress => ({
  wardName: '',
  districtName: '',
  countryName: '',
  localityName: '',
  subLocalityName: '',
  regionName: '',
  routeName: '',
  streetNumber: '',
  postalCode: '',
  administrativeAreaLevel1: '',
  administrativeAreaLevel2: '',
  administrativeAreaLevel3: '',
  administrativeAreaLevel4: '',
  formattedAddress: '',
  placeId: '',
  allFormattedAddresses: [],
});

/**
 * Checks whether an address component contains a specific Google component type.
 */
const hasType = (component: GoogleAddressComponent, type: string): boolean => {
  return Array.isArray(component?.types) && component.types.includes(type);
};

/**
 * ParsedGoogleAddress keys that hold scalar string values.
 */
type AddressScalarKey = Exclude<
  keyof ParsedGoogleAddress,
  'allFormattedAddresses'
>;

/**
 * Assigns a parsed field only when the current value is empty.
 */
const assignIfEmpty = (
  parsed: ParsedGoogleAddress,
  key: AddressScalarKey,
  value?: string
) => {
  if (!parsed[key] && value) {
    parsed[key] = value;
  }
};

/**
 * Applies a single Google address component into the normalized parsed shape.
 * A component can map to multiple fields (for example level 4 also contributes to ward).
 */
const applyComponentToParsed = (
  parsed: ParsedGoogleAddress,
  component: GoogleAddressComponent
) => {
  const value = component?.long_name || '';
  if (!value) return;

  if (hasType(component, 'country')) {
    assignIfEmpty(parsed, 'countryName', value);
  }
  if (hasType(component, 'locality')) {
    assignIfEmpty(parsed, 'localityName', value);
  }
  if (hasType(component, 'sublocality')) {
    assignIfEmpty(parsed, 'subLocalityName', value);
  }
  if (hasType(component, 'sublocality_level_1')) {
    assignIfEmpty(parsed, 'subLocalityName', value);
    assignIfEmpty(parsed, 'wardName', value);
  }
  if (hasType(component, 'sublocality_level_2')) {
    assignIfEmpty(parsed, 'wardName', value);
  }
  if (hasType(component, 'neighborhood')) {
    assignIfEmpty(parsed, 'wardName', value);
  }
  if (hasType(component, 'administrative_area_level_4')) {
    assignIfEmpty(parsed, 'administrativeAreaLevel4', value);
    assignIfEmpty(parsed, 'wardName', value);
  }
  if (hasType(component, 'administrative_area_level_3')) {
    assignIfEmpty(parsed, 'administrativeAreaLevel3', value);
    assignIfEmpty(parsed, 'districtName', value);
  }
  if (hasType(component, 'administrative_area_level_2')) {
    assignIfEmpty(parsed, 'administrativeAreaLevel2', value);
    assignIfEmpty(parsed, 'districtName', value);
  }
  if (hasType(component, 'administrative_area_level_1')) {
    assignIfEmpty(parsed, 'administrativeAreaLevel1', value);
    assignIfEmpty(parsed, 'regionName', value);
  }
  if (hasType(component, 'route')) {
    assignIfEmpty(parsed, 'routeName', value);
  }
  if (hasType(component, 'street_number')) {
    assignIfEmpty(parsed, 'streetNumber', value);
  }
  if (hasType(component, 'postal_code')) {
    assignIfEmpty(parsed, 'postalCode', value);
  }
};

/**
 * Converts result geometry into typed coordinates.
 */
const getCoordinates = (result: GoogleGeocodeResult) => {
  const latValue = result?.geometry?.location?.lat;
  const lngValue = result?.geometry?.location?.lng;

  return {
    lat: latValue !== undefined ? Number(latValue) : undefined,
    lng: lngValue !== undefined ? Number(lngValue) : undefined,
  };
};

/**
 * Normalizes one or more Google geocode results into a single parsed address shape.
 * The first non-empty value per field is retained.
 */
const parseGeocodeResults = (
  results: GoogleGeocodeResult[] = []
): ParsedGoogleAddress => {
  const parsed = defaultParsedAddress();

  parsed.allFormattedAddresses = results
    .map((result) => result?.formatted_address || '')
    .filter(Boolean);

  const primaryResult = results[0];
  if (primaryResult?.formatted_address) {
    parsed.formattedAddress = primaryResult.formatted_address;
  }
  if (primaryResult?.place_id) {
    parsed.placeId = primaryResult.place_id;
  }

  for (const result of results) {
    if (!parsed.formattedAddress && result?.formatted_address) {
      parsed.formattedAddress = result.formatted_address;
    }
    if (!parsed.placeId && result?.place_id) {
      parsed.placeId = result.place_id;
    }

    const components = result?.address_components || [];
    for (const component of components) {
      applyComponentToParsed(parsed, component);
    }
  }

  return parsed;
};

/**
 * Merges a per-result parsed address with an all-results parsed fallback.
 * This helps fill missing metadata when a single Google result is incomplete.
 */
const mergeParsedAddress = (
  parsed: ParsedGoogleAddress,
  fallback: ParsedGoogleAddress
): ParsedGoogleAddress => {
  return {
  ...parsed,
  wardName: parsed.wardName || fallback.wardName,
  districtName: parsed.districtName || fallback.districtName,
  countryName: parsed.countryName || fallback.countryName,
  localityName: parsed.localityName || fallback.localityName,
  subLocalityName: parsed.subLocalityName || fallback.subLocalityName,
  regionName: parsed.regionName || fallback.regionName,
  routeName: parsed.routeName || fallback.routeName,
  streetNumber: parsed.streetNumber || fallback.streetNumber,
  postalCode: parsed.postalCode || fallback.postalCode,
  administrativeAreaLevel1:
    parsed.administrativeAreaLevel1 || fallback.administrativeAreaLevel1,
  administrativeAreaLevel2:
    parsed.administrativeAreaLevel2 || fallback.administrativeAreaLevel2,
  administrativeAreaLevel3:
    parsed.administrativeAreaLevel3 || fallback.administrativeAreaLevel3,
  administrativeAreaLevel4:
    parsed.administrativeAreaLevel4 || fallback.administrativeAreaLevel4,
  formattedAddress: parsed.formattedAddress || fallback.formattedAddress,
  placeId: parsed.placeId || fallback.placeId,
  allFormattedAddresses:
    parsed.allFormattedAddresses?.length > 0
      ? parsed.allFormattedAddresses
      : fallback.allFormattedAddresses,
  };
};

/**
 * Resolves district id and canonical district label from parsed district text.
 * Falls back to configured defaults when no match is found.
 */
const resolveDistrict = (
  districtName: string,
  districts: DistrictOption[] = [],
  defaultDistrictName = 'UpCountry',
  defaultDistrictId = '651d5fa8317c6e0dcdcd8fa1'
) => {
  const normalizedDistrictName = (districtName || '').toLowerCase();
  const match = districts.find((obj) => {
    const name = (obj?.name || '').toLowerCase();
    return normalizedDistrictName && name.includes(normalizedDistrictName);
  });

  if (match?._id && match?.name) {
    return {
      district: match._id,
      districtName: match.name,
      status: true,
    };
  }

  return {
    district: defaultDistrictId,
    districtName: defaultDistrictName,
    status: false,
  };
};

/**
 * Picks the best geocode result for a free-text query.
 * If no query match exists, the first result is used.
 */
const chooseResultBySearchText = (
  results: GoogleGeocodeResult[] = [],
  searchText?: string
): GoogleGeocodeResult | null => {
  if (!results.length) return null;
  if (!searchText?.trim()) return results[0];

  const query = searchText.trim().toLowerCase();
  return (
    results.find((result) => {
      const formatted = (result?.formatted_address || '').toLowerCase();
      if (formatted.includes(query)) return true;

      return (result?.address_components || []).some((component) =>
        (component?.long_name || '').toLowerCase().includes(query)
      );
    }) || results[0]
  );
};

/**
 * Builds the legacy-compatible arrayResponse payload used by existing modules.
 */
const buildArrayResponse = (
  status: boolean,
  dataItems: GoogleAddressItem[]
) => {
  return [
    {
      status,
      data: dataItems.map((item) => ({
        raw: {
          ward: item.wardName || '',
          city: item.localityName || '',
          district: item.districtName || '',
          country: item.countryName || '',
          region: item.parsed?.regionName || '',
          route: item.parsed?.routeName || '',
          street_number: item.parsed?.streetNumber || '',
          postal_code: item.parsed?.postalCode || '',
        },
        full_address: item.fullAddress || '',
        placeid: item.placeId || '',
      })),
    },
  ];
};

/**
 * Reverse-geocodes latitude/longitude and returns normalized address metadata.
 * Supports either a raw search text value or an options object for district matching.
 */
export const searchGoogleAddressUsingLatLong = async (
  lat: string | number,
  lng: string | number,
  searchTextOrOptions?: string | SearchGoogleAddressOptions
): Promise<SearchGoogleAddressResult> => {
  const parsedFallback = defaultParsedAddress();
  const options: SearchGoogleAddressOptions =
    typeof searchTextOrOptions === 'string'
      ? { searchText: searchTextOrOptions }
      : searchTextOrOptions || {};

  const {
    searchText,
    districts = [],
    defaultDistrictName = 'UpCountry',
    defaultDistrictId = '651d5fa8317c6e0dcdcd8fa1',
  } = options;

  try {
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${APIkey}`;
    const response = await fetch(apiUrl);
    const data: GoogleGeocodeResponse = await response.json();

    const results = Array.isArray(data?.results) ? data.results : [];

    // Picks one “best” result from the Google results array
    const selectedResult = chooseResultBySearchText(results, searchText);

    // Reorders results so the selected one is first
    const selectedFirst = selectedResult
      ? [selectedResult, ...results.filter((item) => item !== selectedResult)]
      : results;

    // Parses only the selected result into normalized fields
    const parsedSelected = parseGeocodeResults(
      selectedResult ? [selectedResult] : []
    );

    const parsedFromAllResults = parseGeocodeResults(selectedFirst);

    // Build the final data items array with enriched metadata and district resolution.
    const dataItems: GoogleAddressItem[] = selectedFirst.map((result) => {

      // Merge the parsed result with the fallback from all results to ensure no missing fields
      const parsed = mergeParsedAddress(
        parseGeocodeResults([result]),
        parsedFromAllResults
      );

      const { lat, lng } = getCoordinates(result);
      const districtInfo = resolveDistrict(
        parsed.districtName,
        districts,
        defaultDistrictName,
        defaultDistrictId
      );

      return {
        status: districtInfo.status,
        isSelected: selectedResult ? result === selectedResult : false,
        placeId: result?.place_id || parsed.placeId || '',
        fullAddress: result?.formatted_address || parsed.formattedAddress || '',
        district: districtInfo.district,
        districtName: districtInfo.districtName,
        wardName: parsed.wardName || '',
        localityName: parsed.localityName || '',
        countryName: parsed.countryName || '',
        lat,
        lng,
        parsed,
        raw: result,
      };
    });

    const finalResponse = {
      status: results.length > 0,
      rawResults: results,
      selectedResult,
      parsed: parsedSelected,
      data: dataItems,
      arrayResponse: buildArrayResponse(results.length > 0, dataItems),
    };
    return finalResponse;
  } catch (error) {
    const fallbackResponse = {
      status: false,
      rawResults: [],
      selectedResult: null,
      parsed: parsedFallback,
      data: [],
      arrayResponse: buildArrayResponse(false, []),
    };

    return fallbackResponse;
  }
};

/**
 * Backward-compatible alias for legacy imports using lower-case naming.
 */
export const searchgoogleaddressusinglatlong = searchGoogleAddressUsingLatLong;
